import React, { useState, useContext, useEffect } from "react";
import Stepper from "Components/stepper3/Stepper";
import { useHistory } from "react-router-dom";
import Type from "./add_form/Type";
import Plan from "./add_form/Plan";
import useFormState from "Utils/useFormState";
import useDeliveryPrices from "Utils/useDeliveryPrices";
import useSteps from "Utils/useSteps";
import Recap from "./add_form/Recap";
import Credentials from "./add_form/Credentials";
import QboxmailApi from "@/qboxmailApi";
import Loader from "Components/loader/Loader";
import CustomerTypeSelection from "./add_form/CustomerTypeSelection";
import BillingInfo from "./add_form/BillingInfo";
import PaymentMethod from "./add_form/PaymentMethod";
import useSWR from "swr";
import { AuthContext } from "@/App";
import { deliveryPlans } from "@/config";

const api = new QboxmailApi({ useCookie: true });

const NO_BILLING_INFO_STEPS = [Type, Plan, PaymentMethod, Recap, Credentials];
const BILLING_INFO_STEPS = [Type, Plan, CustomerTypeSelection, BillingInfo, PaymentMethod, Recap, Credentials];
const NO_PAYMENT_METHOD_STEPS = [Type, Plan, Recap, Credentials];

function AccountNewScreen(props) {
  const { isLoggedUserAdmin, getLoggedUser } = useContext(AuthContext);
  const loggedUser = getLoggedUser();
  const isReseller = getLoggedUser().is_reseller;
  const isManager = loggedUser.type === "Manager";

  const { prices } = useDeliveryPrices();
  const { data: userBillingInfo } = useSWR(`/users/billing`, null, {
    revalidateOnFocus: false,
  });

  // if is admin we get the limits later in the wizard, once we know the code
  const { data: limits } = useSWR(!isLoggedUserAdmin() && `/users/delivery_limits`, null, {
    revalidateOnFocus: false,
  });

  const [selectedTier, setSelectedTier] = useState(null);
  const [canHaveFreeAccount, setCanHaveFreeAccount] = useState(true);
  const [hideFreeAccountPicker, setHideFreeAccountPicker] = useState(false);
  const [hidePrices, setHidePrices] = useState(false);
  const [steps, setSteps] = useState([]);

  const formState = useFormState({
    type: "transactional",
    plan: limits?.can_create_free_account || isLoggedUserAdmin() ? "free" : "monthly",
    payment_method: "stripe",
    volume: 0,
  });

  useEffect(() => {
    if (!limits) return;
    setCanHaveFreeAccount(limits.can_create_free_account);
    setHideFreeAccountPicker(limits.hide_free_account_picker);
    setHidePrices(limits.hide_prices);

    formState.setFormFieldValue({
      plan: limits.can_create_free_account ? "free" : "monthly",
    });
  }, [limits]);

  useEffect(() => {
    if (isLoggedUserAdmin() || isManager) {
      setSteps(NO_PAYMENT_METHOD_STEPS);
      return;
    }

    if (formState.formData.plan === deliveryPlans.FREE && userBillingInfo) {
      setSteps(NO_PAYMENT_METHOD_STEPS);
    } else if (userBillingInfo) {
      setSteps(NO_BILLING_INFO_STEPS);
    } else {
      setSteps(BILLING_INFO_STEPS);
    }
  }, [formState.formData.plan, userBillingInfo]);

  const history = useHistory();

  const { step, ToRender, stepBack, nextStep, goToStep, stepsCount } = useSteps(steps);
  const [newUserData, setNewUserData] = useState({});

  const backToList = () => {
    history.push("/delivery/accounts");
  };

  if (!prices || !ToRender) {
    return <Loader />;
  }

  return (
    <div className="contentWrapper">
      <ToRender
        {...props}
        api={api}
        hasBillingInfo={!!userBillingInfo}
        onFormCancel={backToList}
        limits={limits}
        selectedTier={selectedTier}
        setSelectedTier={setSelectedTier}
        goToStep={goToStep}
        setCanHaveFreeAccount={setCanHaveFreeAccount}
        canHaveFreeAccount={canHaveFreeAccount}
        hideFreeAccountPicker={hideFreeAccountPicker}
        hidePrices={hidePrices}
        nextStep={nextStep}
        stepper={<Stepper className="max-w-[--form-page-width]" steps={stepsCount} active={step} goBack={stepBack} />}
        {...formState}
        newUserData={newUserData}
        setNewUserData={setNewUserData}
        prices={prices}
        loggedUser={loggedUser}
        isReseller={isReseller}
        isLoggedUserAdmin={isLoggedUserAdmin()}
      />
    </div>
  );
}

export default AccountNewScreen;
